import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken, getUsuarioSistemaId } from '../utils/auth';
import GerenciarAdminPopup from './GerenciarAdminPopup';
import RemoverMembrosPopup from './RemoverMembrosPopup';
import BASE_URL from '../utils/api';

// API helper
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const bases = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
  ];
  for (const base of bases) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = getAuthToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const cfg = { method, headers };
      if (data && (method === 'POST' || method === 'PUT')) cfg.body = JSON.stringify(data);
      const res = await fetch(base, cfg);
      if (!res) continue;
      if (res.ok || (res.status >= 200 && res.status < 300)) {
        const ct = res.headers.get('Content-Type') || '';
        const text = await res.text();
        if (ct.includes('application/json')) {
          try { return JSON.parse(text); } catch { return text; }
        }
        return text;
      }
    } catch (e) { /* try next base */ }
  }
  throw new Error(`Nenhuma URL funcionou para ${method} ${endpoint}`);
};

// Extrai primeira letra válida (sem números ou caracteres especiais)
const getInitial = (name) => {
  if (!name) return '?';
  const clean = name.replace(/[^a-zA-ZÀ-ÿ]/g, '');
  return clean.charAt(0).toUpperCase() || '?';
};

export default function MembrosGrupoPopup({ grupoId, donoId, isOwner, isAdmin, onClose, onMembersChanged }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [membros, setMembros] = useState([]);
  const [membrosLoading, setMembrosLoading] = useState(false);
  const [carrinho, setCarrinho] = useState([]); // usuários selecionados para adicionar
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [showGerenciarAdmin, setShowGerenciarAdmin] = useState(false);
  const [showRemoverMembros, setShowRemoverMembros] = useState(false);
  const [filterMembros, setFilterMembros] = useState('');
  
  // Estados para Minhas Conexões
  const [minhasConexoes, setMinhasConexoes] = useState([]);
  const [conexoesLoading, setConexoesLoading] = useState(false);
  
  // Pode gerenciar = dono ou admin
  const canManage = isOwner || isAdmin;

  // Carregar membros atuais do grupo
  const carregarMembros = useCallback(async () => {
    if (!grupoId) return;
    setMembrosLoading(true);
    try {
      const res = await apiCall(`UsuariosGrupo/grupo/${grupoId}`, 'GET');
      const arr = Array.isArray(res) ? res : [];
      // Para cada membro, buscar dados do usuário e login
      const membrosComDados = await Promise.all(arr.map(async (m) => {
        try {
          const usId = m.UsuarioSistemaId || m.usuarioSistemaId;
          const dados = await apiCall(`DadosUsuario/por-usuario/${usId}`, 'GET');
          // Buscar login do UsuarioSistema
          let login = '';
          try {
            const usuarioSistema = await apiCall(`UsuarioSistema/${usId}`, 'GET');
            login = usuarioSistema?.Login || usuarioSistema?.login || '';
          } catch { /* ignora */ }
          return {
            ...m,
            nomeUsuario: dados?.NomeUsuario || dados?.nomeUsuario || dados?.Nome || dados?.nome || 'Usuário',
            usuarioSistemaId: usId,
            login: login,
          };
        } catch {
          return { ...m, nomeUsuario: 'Usuário', usuarioSistemaId: m.UsuarioSistemaId || m.usuarioSistemaId, login: '' };
        }
      }));
      setMembros(membrosComDados);
    } catch (err) {
      console.error('Erro ao carregar membros', err);
      setMembros([]);
    } finally {
      setMembrosLoading(false);
    }
  }, [grupoId]);

  useEffect(() => {
    carregarMembros();
  }, [carregarMembros]);

  // Carregar minhas conexões
  const carregarMinhasConexoes = useCallback(async () => {
    setConexoesLoading(true);
    try {
      const meuId = getUsuarioSistemaId();
      if (!meuId) {
        setMinhasConexoes([]);
        return;
      }
      
      // Buscar conexões onde eu sou a origem
      const conexoes = await apiCall('ConexaoUsuario', 'GET');
      const arrConexoes = Array.isArray(conexoes) ? conexoes : [];
      const minhasConexoesIds = arrConexoes
        .filter(c => (c.UsuarioIdOrigem || c.usuarioIdOrigem) === meuId)
        .map(c => c.UsuarioIdDestino || c.usuarioIdDestino);
      
      if (minhasConexoesIds.length === 0) {
        setMinhasConexoes([]);
        return;
      }
      
      // IDs de membros já no grupo e no carrinho para excluir
      const membrosIds = new Set(membros.map(m => m.usuarioSistemaId || m.UsuarioSistemaId));
      const carrinhoIds = new Set(carrinho.map(c => c.usuarioSistemaId));
      
      // Buscar dados dos usuários conectados
      const usuariosConectados = [];
      for (const usuarioSistemaId of minhasConexoesIds) {
        // Excluir se já é membro ou está no carrinho
        if (membrosIds.has(usuarioSistemaId) || carrinhoIds.has(usuarioSistemaId)) continue;
        
        try {
          // Buscar login
          const usuarioSistema = await apiCall(`UsuarioSistema/${usuarioSistemaId}`, 'GET');
          const login = usuarioSistema?.Login || usuarioSistema?.login || '';
          
          // Buscar nome via DadosUsuario
          let nomeCompleto = login;
          try {
            const dadosUsuario = await apiCall(`DadosUsuario/por-usuario/${usuarioSistemaId}`, 'GET');
            nomeCompleto = dadosUsuario?.nomeUsuario || dadosUsuario?.NomeUsuario || dadosUsuario?.Nome || dadosUsuario?.nome || login;
          } catch { /* mantém login como nome */ }
          
          usuariosConectados.push({
            usuarioSistemaId: usuarioSistemaId,
            nomeUsuario: nomeCompleto,
            login: login
          });
        } catch (e) {
          console.error('Erro ao buscar dados do usuário:', e);
        }
      }
      
      setMinhasConexoes(usuariosConectados);
    } catch (err) {
      console.error('Erro ao carregar conexões:', err);
      setMinhasConexoes([]);
    } finally {
      setConexoesLoading(false);
    }
  }, [membros, carrinho]);

  useEffect(() => {
    if (canManage) {
      carregarMinhasConexoes();
    }
  }, [carregarMinhasConexoes, canManage]);

  // Buscar usuários dinamicamente
  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      setSearchResults([]);
      return;
    }
    const buscar = async () => {
      setSearching(true);
      try {
        const res = await apiCall('DadosUsuario', 'GET');
        const arr = Array.isArray(res) ? res : [];
        const termLower = searchTerm.toLowerCase();
        const filtered = arr.filter(u => {
          const nome = u.NomeUsuario || u.nomeUsuario || u.Nome || u.nome || '';
          return nome.toLowerCase().includes(termLower);
        });
        // Excluir usuários que já são membros ou que já estão no carrinho
        const membrosIds = new Set(membros.map(m => m.usuarioSistemaId || m.UsuarioSistemaId));
        const carrinhoIds = new Set(carrinho.map(c => c.usuarioSistemaId));
        const finalFiltered = filtered.filter(u => {
          const uid = u.UsuarioSistemaId || u.usuarioSistemaId;
          return !membrosIds.has(uid) && !carrinhoIds.has(uid);
        });
        
        // Buscar login para cada usuário encontrado
        const resultadosComLogin = await Promise.all(finalFiltered.map(async (u) => {
          const uid = u.UsuarioSistemaId || u.usuarioSistemaId;
          let login = '';
          try {
            const usuarioSistema = await apiCall(`UsuarioSistema/${uid}`, 'GET');
            login = usuarioSistema?.Login || usuarioSistema?.login || '';
          } catch { /* ignora */ }
          return { ...u, login };
        }));
        
        setSearchResults(resultadosComLogin);
      } catch (err) {
        console.error('Erro ao buscar usuários', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };
    const timer = setTimeout(buscar, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, membros, carrinho]);

  // Adicionar ao carrinho
  const adicionarAoCarrinho = (usuario) => {
    const uid = usuario.UsuarioSistemaId || usuario.usuarioSistemaId;
    const nome = usuario.NomeUsuario || usuario.nomeUsuario || usuario.Nome || usuario.nome || 'Usuário';
    const login = usuario.login || usuario.Login || '';
    if (carrinho.find(c => c.usuarioSistemaId === uid)) return;
    setCarrinho(prev => [...prev, { usuarioSistemaId: uid, nomeUsuario: nome, login: login }]);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Remover do carrinho
  const removerDoCarrinho = (uid) => {
    setCarrinho(prev => prev.filter(c => c.usuarioSistemaId !== uid));
  };

  // Adicionar usuários ao grupo
  const adicionarUsuarios = async () => {
    if (carrinho.length === 0) return;
    setAdding(true);
    setError('');
    try {
      for (const item of carrinho) {
        await apiCall('UsuariosGrupo', 'POST', {
          GrupoId: grupoId,
          UsuarioSistemaId: item.usuarioSistemaId,
          AdminGrupo: 0
        });
      }
      setCarrinho([]);
      await carregarMembros();
      if (onMembersChanged) onMembersChanged();
    } catch (err) {
      console.error('Erro ao adicionar usuários', err);
      setError('Erro ao adicionar usuários');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="membros-popup-overlay" onClick={(e) => { if (e.target.classList.contains('membros-popup-overlay')) onClose && onClose(); }}>
      <div className="membros-popup-card" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Membros do Grupo</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Fechar" style={{ fontSize: 24, background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(235, 133, 0)' }}>×</button>
        </div>

        {/* Seção Adicionar Membros - só para dono e admins */}
        {canManage && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#333' }}>Adicionar Membros</h4>
          <input
            type="text"
            className="form-input"
            placeholder="Digite o nome do usuário..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
          />
          
          {/* Resultados da busca */}
          {searchTerm && (
            <div className="membros-search-results">
              {searching && <div style={{ color: '#666', padding: 8 }}>Buscando...</div>}
              {!searching && searchResults.length === 0 && searchTerm.length > 0 && (
                <div style={{ color: '#999', padding: 8 }}>Nenhum usuário encontrado</div>
              )}
              {!searching && searchResults.map(u => {
                const uid = u.UsuarioSistemaId || u.usuarioSistemaId;
                const nome = u.NomeUsuario || u.nomeUsuario || u.Nome || u.nome || 'Usuário';
                const login = u.login || u.Login || '';
                return (
                  <div
                    key={uid}
                    className="membros-search-item"
                    onClick={() => adicionarAoCarrinho(u)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="membros-avatar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff' }}>
                      {getInitial(nome)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="membros-nome">{nome}</span>
                      {login && <span style={{ fontSize: 12, color: 'rgb(235, 133, 0)' }}>{login}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Carrinho de usuários selecionados */}
          {carrinho.length > 0 && (
            <div className="membros-carrinho">
              {carrinho.map(c => (
                <div key={c.usuarioSistemaId} className="membros-carrinho-item">
                  <div className="membros-avatar-small" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff' }}>
                    {getInitial(c.nomeUsuario)}
                  </div>
                  <span className="membros-carrinho-nome">{c.nomeUsuario}</span>
                  <button
                    className="membros-carrinho-remove"
                    onClick={() => removerDoCarrinho(c.usuarioSistemaId)}
                    aria-label="Remover"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Botão Adicionar */}
          {carrinho.length > 0 && (
            <button
              className="button-create"
              onClick={adicionarUsuarios}
              disabled={adding}
              style={{ marginTop: 12, width: '100%' }}
            >
              {adding ? 'Adicionando...' : carrinho.length === 1 ? 'Adicionar Usuário' : 'Adicionar Usuários'}
            </button>
          )}

          {error && <div style={{ color: '#d32f2f', marginTop: 8 }}>{error}</div>}
          
          {/* Seção Minhas Conexões */}
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#333' }}>Minhas Conexões</h4>
            <div className="membros-lista-container" style={{ maxHeight: 200, overflowY: 'auto' }}>
              {conexoesLoading && <div style={{ color: '#666', padding: 8 }}>Carregando conexões...</div>}
              {!conexoesLoading && minhasConexoes.length === 0 && (
                <div style={{ color: '#999', padding: 8 }}>Nenhuma conexão disponível para adicionar</div>
              )}
              {!conexoesLoading && minhasConexoes.map(c => (
                <div
                  key={c.usuarioSistemaId}
                  className="membros-search-item"
                  onClick={() => adicionarAoCarrinho(c)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="membros-avatar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff' }}>
                    {getInitial(c.nomeUsuario)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="membros-nome">{c.nomeUsuario}</span>
                    {c.login && <span style={{ fontSize: 12, color: 'rgb(235, 133, 0)' }}>{c.login}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Lista de Membros */}
        <div>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#333' }}>Membros</h4>
          {/* Filtro de membros */}
          <input
            type="text"
            className="form-input"
            placeholder="Filtrar membros..."
            value={filterMembros}
            onChange={(e) => setFilterMembros(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <div className="membros-lista-container">
            {membrosLoading && <div style={{ color: '#666', padding: 8 }}>Carregando membros...</div>}
            {!membrosLoading && membros.length === 0 && (
              <div style={{ color: '#999', padding: 8 }}>Nenhum membro no grupo</div>
            )}
            {!membrosLoading && membros
              .filter(m => {
                if (!filterMembros) return true;
                const nome = m.nomeUsuario || '';
                return nome.toLowerCase().includes(filterMembros.toLowerCase());
              })
              .map(m => {
              const nome = m.nomeUsuario || 'Usuário';
              const login = m.login || '';
              const isAdminMembro = m.AdminGrupo === 1 || m.adminGrupo === 1;
              return (
                <div key={m.Id || m.id || m.usuarioSistemaId} className="membros-lista-item">
                  <div className="membros-avatar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff' }}>
                    {getInitial(nome)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <span className="membros-nome">{nome}</span>
                    {login && <span style={{ fontSize: 12, color: 'rgb(235, 133, 0)' }}>{login}</span>}
                  </div>
                  {isAdminMembro && <span className="membros-admin-badge">Admin</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Botões de gerenciamento - só para dono e admins */}
        {canManage && (
          <>
            {/* Botão Gerenciar Admin */}
            <button
              className="btn-gerenciar-admin"
              onClick={() => setShowGerenciarAdmin(true)}
              style={{ background: 'rgb(235, 133, 0)', color: '#fff', border: 'none' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
              </svg>
              Gerenciar Admin
            </button>

            {/* Botão Remover Membros */}
            <button
              className="btn-gerenciar-admin"
              onClick={() => setShowRemoverMembros(true)}
              style={{ background: '#d32f2f', color: '#fff', border: 'none', marginTop: 8 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 4V3H9v1H4v2h1v13c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V6h1V4h-5zm2 15H7V6h10v13z" fill="currentColor"/>
                <path d="M9 8h2v9H9zM13 8h2v9h-2z" fill="currentColor"/>
              </svg>
              Remover Membros
            </button>
          </>
        )}

        {/* Popup de Gerenciar Admin */}
        {showGerenciarAdmin && (
          <GerenciarAdminPopup
            grupoId={grupoId}
            donoId={donoId}
            isOwner={isOwner}
            onClose={() => setShowGerenciarAdmin(false)}
            onAdminsChanged={() => {
              carregarMembros();
              if (onMembersChanged) onMembersChanged();
            }}
          />
        )}

        {/* Popup de Remover Membros */}
        {showRemoverMembros && (
          <RemoverMembrosPopup
            grupoId={grupoId}
            donoId={donoId}
            isOwner={isOwner}
            onClose={() => setShowRemoverMembros(false)}
            onMembersChanged={() => {
              carregarMembros();
              if (onMembersChanged) onMembersChanged();
            }}
          />
        )}
      </div>
    </div>
  );
}
